/**
 * CopilotProvider — wraps @github/copilot-sdk behind the LLMProvider interface.
 *
 * Extracts the Copilot-specific logic from BaseAgent into a reusable provider
 * that can be swapped with OpenAI or Claude providers via the abstraction layer.
 *
 * NOTE: Vision support for tool results is not available in the Copilot provider.
 * The @github/copilot-sdk handles tool calls internally, so we cannot inject
 * images into the conversation. Tools returning imagePath will have the path
 * included in the JSON result as text only.
 */

import { createCopilotClient } from './ai.js'
import type { SessionEvent } from './ai.js'
import type { CopilotClient, CopilotSession } from '../../L1-infra/ai/copilot.js'
import { approveAll, resolveCopilotCliPath } from '../../L1-infra/ai/copilot.js'
import logger from '../../L1-infra/logger/configLogger.js'
import type {
  LLMProvider,
  LLMSession,
  LLMResponse,
  SessionConfig,
  TokenUsage,
  CostInfo,
  QuotaSnapshot,
  ToolCall,
  ProviderEvent,
  ProviderEventType,
  UserInputRequest,
} from './types'

const DEFAULT_MODEL = 'claude-opus-4.5'
const DEFAULT_TIMEOUT_MS = 300_000 // 5 minutes
const SESSION_CREATE_TIMEOUT_MS = 30_000 // 30 seconds — createSession can hang when Copilot SDK can't connect

export class CopilotProvider implements LLMProvider {
  readonly name = 'copilot' as const
  private client: CopilotClient | null = null

  isAvailable(): boolean {
    // Copilot uses GitHub auth, not an API key
    return true
  }

  getDefaultModel(): string {
    return DEFAULT_MODEL
  }

  async createSession(config: SessionConfig): Promise<LLMSession> {
    if (!this.client) {
      const cliPath = resolveCopilotCliPath()
      if (cliPath) {
        logger.info(`[CopilotProvider] Using native CLI binary: ${cliPath}`)
      }
      this.client = createCopilotClient({
        autoStart: true,
        autoRestart: true,
        logLevel: 'error',
        env: buildChildEnv(),
        ...(cliPath ? { cliPath } : {}),
      })
    }

    logger.info('[CopilotProvider] Creating session…')

    let copilotSession: CopilotSession
    try {
      copilotSession = await new Promise<CopilotSession>((resolve, reject) => {
        const timeoutId = setTimeout(
          () => reject(new Error(
            `[CopilotProvider] createSession timed out after ${SESSION_CREATE_TIMEOUT_MS / 1000}s — ` +
            'the Copilot SDK language server may not be reachable. ' +
            'Check GitHub authentication and network connectivity.'
          )),
          SESSION_CREATE_TIMEOUT_MS,
        )
        this.client!.createSession({
          model: config.model,
          mcpServers: config.mcpServers,
          systemMessage: { mode: 'replace', content: config.systemPrompt },
          tools: config.tools.map((t) => ({
            name: t.name,
            description: t.description,
            parameters: t.parameters,
            handler: t.handler,
          })),
          streaming: config.streaming ?? true,
          onPermissionRequest: approveAll,
          onUserInputRequest: config.onUserInputRequest
            ? (request: UserInputRequest) => config.onUserInputRequest!(request)
            : undefined,
        }).then(
          (session) => { clearTimeout(timeoutId); resolve(session) },
          (err) => { clearTimeout(timeoutId); reject(err) },
        )
      })
    } catch (err) {
      this.client = null
      throw err
    }

    logger.info('[CopilotProvider] Session created successfully')

    return new CopilotSessionWrapper(
      copilotSession,
      config.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    )
  }

  /** Tear down the underlying Copilot client. */
  async close(): Promise<void> {
    try {
      if (this.client) {
        await this.client.stop()
        this.client = null
      }
    } catch (err) {
      logger.error(`[CopilotProvider] Error during close: ${err}`)
    }
  }
}

/** Wraps a CopilotSession to satisfy the LLMSession interface. */
class CopilotSessionWrapper implements LLMSession {
  private eventHandlers = new Map<ProviderEventType, Array<(event: ProviderEvent) => void>>()

  // Latest usage data captured from assistant.usage events
  private lastUsage: TokenUsage = { inputTokens: 0, outputTokens: 0, totalTokens: 0 }
  private lastCost: CostInfo | undefined
  private lastQuotaSnapshots: Record<string, QuotaSnapshot> | undefined
  
  // Track tool completions to handle partial success on SDK errors
  private toolsCompleted = 0

  constructor(
    private readonly session: CopilotSession,
    private readonly timeoutMs: number,
  ) {
    this.setupEventForwarding()
    this.setupUsageTracking()
  }

  async sendAndWait(message: string): Promise<LLMResponse> {
    const start = Date.now()

    // Reset usage tracking for this call
    this.lastUsage = { inputTokens: 0, outputTokens: 0, totalTokens: 0 }
    this.lastCost = undefined
    this.lastQuotaSnapshots = undefined
    this.toolsCompleted = 0

    let response: { data?: { content?: string } } | undefined
    let sdkError: Error | undefined

    try {
      if (this.timeoutMs === 0) {
        // No timeout — use send() + session.idle for interactive agents
        // that block on user input inside tool handlers.
        response = await this.sendAndWaitForIdle(message)
      } else {
        response = await this.session.sendAndWait(
          { prompt: message },
          this.timeoutMs,
        )
      }
    } catch (err) {
      sdkError = err instanceof Error ? err : new Error(String(err))
      
      // Handle the known "missing finish_reason" bug in @github/copilot SDK
      // This happens when the streaming response ends without proper termination
      // but tools may have already completed successfully
      if (sdkError.message.includes('missing finish_reason')) {
        if (this.toolsCompleted > 0) {
          logger.warn(`[CopilotProvider] SDK error after ${this.toolsCompleted} tool calls completed - treating as success`)
          // Return partial success - tools ran, just the final message was lost
        } else {
          // No tools completed, this is a real failure - rethrow
          throw sdkError
        }
      } else {
        throw sdkError
      }
    }

    const content = response?.data?.content ?? ''
    const toolCalls: ToolCall[] = [] // Copilot SDK handles tool calls internally

    return {
      content,
      toolCalls,
      usage: this.lastUsage,
      cost: this.lastCost,
      quotaSnapshots: this.lastQuotaSnapshots,
      durationMs: Date.now() - start,
    }
  }

  /**
   * Send a message and wait for session.idle without any timeout.
   * Used by interactive agents (interview, chat) where tool handlers
   * block waiting for human input — the SDK's sendAndWait() timeout
   * would fire while the agent is legitimately waiting for the user.
   */
  private sendAndWaitForIdle(message: string): Promise<{ data?: { content?: string } } | undefined> {
    return new Promise<{ data?: { content?: string } } | undefined>((resolve, reject) => {
      let lastAssistantMessage: { data?: { content?: string } } | undefined

      const unsubMessage = this.session.on('assistant.message', (event: { data?: { content?: string } }) => {
        lastAssistantMessage = event
      })

      const unsubIdle = this.session.on('session.idle', () => {
        unsubMessage()
        unsubIdle()
        unsubError()
        resolve(lastAssistantMessage)
      })

      const unsubError = this.session.on('session.error', (event: { data?: { message?: string } }) => {
        unsubMessage()
        unsubIdle()
        unsubError()
        reject(new Error(event.data?.message ?? 'Unknown session error'))
      })

      this.session.send({ prompt: message }).catch((err: unknown) => {
        unsubMessage()
        unsubIdle()
        unsubError()
        reject(err instanceof Error ? err : new Error(String(err)))
      })
    })
  }

  on(event: ProviderEventType, handler: (event: ProviderEvent) => void): void {
    const handlers = this.eventHandlers.get(event) ?? []
    handlers.push(handler)
    this.eventHandlers.set(event, handlers)
  }

  async close(): Promise<void> {
    // Add timeout to session.destroy() - it can hang on the same SDK bug
    const DESTROY_TIMEOUT_MS = 5000
    try {
      await Promise.race([
        this.session.destroy(),
        new Promise<void>((_, reject) => 
          setTimeout(() => reject(new Error('session.destroy() timed out')), DESTROY_TIMEOUT_MS)
        ),
      ])
    } catch (err) {
      // Log but don't rethrow - the session may be in a bad state but we still want to clean up
      logger.warn(`[CopilotProvider] Session destroy failed: ${err instanceof Error ? err.message : String(err)}`)
    }
    this.eventHandlers.clear()
  }

  /** Capture assistant.usage events for token/cost tracking. */
  private setupUsageTracking(): void {
    this.session.on((event: SessionEvent) => {
      if (event.type === 'assistant.usage') {
        const d = event.data as unknown as Record<string, unknown>
        this.lastUsage = {
          inputTokens: (d.inputTokens as number) ?? 0,
          outputTokens: (d.outputTokens as number) ?? 0,
          totalTokens: ((d.inputTokens as number) ?? 0) + ((d.outputTokens as number) ?? 0),
          cacheReadTokens: d.cacheReadTokens as number | undefined,
          cacheWriteTokens: d.cacheWriteTokens as number | undefined,
        }
        if (d.cost != null) {
          this.lastCost = {
            amount: d.cost as number,
            unit: 'premium_requests',
            model: (d.model as string) ?? DEFAULT_MODEL,
            multiplier: d.multiplier as number | undefined,
          }
        }
        if (d.quotaSnapshots != null) {
          this.lastQuotaSnapshots = d.quotaSnapshots as Record<string, QuotaSnapshot>
        }
      }
    })
  }

  /** Forward CopilotSession events to ProviderEvent subscribers. */
  private setupEventForwarding(): void {
    this.session.on((event: SessionEvent) => {
      switch (event.type) {
        case 'assistant.message_delta':
          this.emit('delta', event.data)
          break
        case 'tool.execution_start':
          this.emit('tool_start', event.data)
          break
        case 'tool.execution_complete':
          this.toolsCompleted++
          this.emit('tool_end', event.data)
          break
        case 'assistant.usage':
          this.emit('usage', event.data)
          break
        case 'session.error':
          this.emit('error', event.data)
          break
      }
    })
  }

  private emit(type: ProviderEventType, data: unknown): void {
    const handlers = this.eventHandlers.get(type)
    if (handlers) {
      for (const handler of handlers) {
        handler({ type, data })
      }
    }
  }
}

/**
 * Build a child-process env that suppresses Node.js ExperimentalWarning on stderr.
 *
 * The @github/copilot-sdk treats ANY stderr output as a fatal error when the CLI
 * subprocess exits, even with code 0. Node.js 24+ emits an ExperimentalWarning
 * for SQLite to stderr, which causes `CLI server exited with code 0\nstderr: ...`.
 * See: https://github.com/htekdev/vidpipe/issues/54
 */
function buildChildEnv(): Record<string, string | undefined> {
  const env = { ...process.env }
  const flag = '--disable-warning=ExperimentalWarning'
  const current = env.NODE_OPTIONS ?? ''
  if (!current.includes(flag)) {
    env.NODE_OPTIONS = current ? `${current} ${flag}` : flag
  }
  return env
}
